<template>
  <div>
    <div :id="gridId"></div>
    <p v-if="!rows || !rows.length" class="p-4 text-grey">No data.</p>
  </div>
</template>

<script>
// Same Syncfusion ej2-vue-grids setup used by the other Quick report pages
// (see QuickBalancing.vue) - an imperative Grid instance appended to a plain
// div, rather than the declarative <ejs-grid> component, since that's the
// convention already established across this app. Columns are built from
// whatever keys the current result set happens to have, since each
// BlackRoyalty tab's stored proc returns a different shape.
import Vue from "vue";
import {
  GridPlugin,
  Grid,
  Page,
  Sort,
  Search,
  Toolbar,
  PdfExport,
  ExcelExport
} from "@syncfusion/ej2-vue-grids";

Vue.use(GridPlugin);
Grid.Inject(Sort, Search, Page, Toolbar, PdfExport, ExcelExport);

let gridIdCounter = 0;

export default {
  name: "DynamicResultTable",
  props: {
    rows: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      gridId: `dynamic-result-grid-${gridIdCounter++}`,
      grid: null
    };
  },
  watch: {
    rows() {
      this.renderGrid();
    }
  },
  mounted() {
    this.renderGrid();
  },
  beforeDestroy() {
    if (this.grid) {
      this.grid.destroy();
      this.grid = null;
    }
  },
  methods: {
    buildColumns() {
      if (!this.rows || !this.rows.length) return [];
      return Object.keys(this.rows[0]).map((key) => {
        // Proc columns like "% of Total", "% of Pit", and "Variance %
        // (worse)" come back as plain fractions (0.3478, not 34.78) - the
        // formatting has to happen inside valueAccessor itself (returning
        // an already-formatted string), since the grid's own `format`
        // property is not applied on top of a valueAccessor's return value.
        // Sorting is unaffected: the grid sorts via the raw field value,
        // not through valueAccessor, so numeric sort order stays correct.
        const isPercent = key.includes("%");

        return {
          // Syncfusion's Grid treats a "." in `field` as a nested-property
          // path ("a.b" -> row.a.b) - "Equip No." (a real proc column name)
          // would resolve to nothing and render blank without this, so the
          // field is sanitized and valueAccessor reads the real key instead.
          field: key.replace(/\./g, "_"),
          headerText: key,
          width: 160,
          valueAccessor: (field, data) => {
            const value = data[key];
            const resolved = value === null || value === undefined ? 0 : value;
            return isPercent ? `${(resolved * 100).toFixed(1)}%` : resolved;
          }
        };
      });
    },
    // Same switch-on-text handling as QuickBalancing.vue's toolbarClick -
    // "Print" is deliberately left unhandled here too, matching that page,
    // since the grid's own built-in toolbar action handles it.
    toolbarClick(args) {
      switch (args.item.text) {
        case "PDF Export":
          this.grid.pdfExport({ fileName: "export.pdf" });
          break;
        case "Excel Export":
          this.grid.excelExport({ fileName: "export.xlsx" });
          break;
        case "CSV Export":
          this.grid.csvExport({ fileName: "export.csv" });
          break;
      }
    },
    // Plain, non-reactive copy of the rows: handing the grid Vue's reactive
    // array (with its getter/setter-wrapped indices) directly can interfere
    // with the grid's own internal paging/sorting logic.
    cloneRows() {
      return this.rows.map((row) => Object.assign({}, row));
    },
    // Always destroy and rebuild rather than reassigning .columns/.dataSource
    // on the existing instance - each BlackRoyalty tab can have a completely
    // different column set (unlike QuickBalancing.vue's fixed columns), and
    // partially updating a live grid leaves the pager's internal state (which
    // page you're on, how many pages exist) out of sync with the new data,
    // which is what made "Refresh" sometimes leave later pages unreachable.
    renderGrid() {
      if (this.grid) {
        this.grid.destroy();
        this.grid = null;
      }

      if (!this.rows || !this.rows.length) return;

      this.grid = new Grid({
        dataSource: this.cloneRows(),
        columns: this.buildColumns(),
        allowPaging: true,
        allowSorting: true,
        allowExcelExport: true,
        allowPdfExport: true,
        toolbar: ["ExcelExport", "CsvExport", "Print", "Search"],
        toolbarClick: this.toolbarClick,
        pageSettings: { pageSize: 20, currentPage: 1 }
      });
      this.grid.appendTo("#" + this.gridId);
    }
  }
};
</script>

<style>
/* Not scoped deliberately: the grid's DOM is built imperatively via
   appendTo(), outside Vue's template compilation, so scoped styles'
   data-attribute selectors would never match it. None of the other
   pages using this same Syncfusion Grid actually load its theme CSS
   (every reference elsewhere in this app is commented out) - which is
   why the header row renders with no visual distinction from the data
   rows without this.

   Two imports, not one: ej2-base/styles/material.css is the shared base
   theme - it's where the toolbar's actual layout CSS (.e-toolbar and
   friends) AND the "e-icons" @font-face live. ej2-vue-grids/styles has
   none of that - it only carries grid-specific rules (rows, headers,
   pager) and assumes the base theme is already loaded alongside it.
   Missing the base theme is exactly why the toolbar buttons stacked
   vertically with "tofu box" icons instead of a proper icon row. */
@import "~@syncfusion/ej2-base/styles/material.css";
@import "~@syncfusion/ej2-vue-grids/styles/material.css";
</style>
